<script setup lang="ts">
import { useDark } from '@vueuse/core';
import { PropType, computed } from 'vue';
import AppDivider from '../app-divider.vue';
import AppButton from '../common/app-button.vue';
import cancelImg from '@/assets/icons/delete01.png';
import cancelImgDark from '@/assets/icons/delete.svg';
import addImg from '../../../assets/icons/plus-circle.svg';
import addImgDark from '../../../assets/icons/plus-circle-dark.svg';
import { DYNAMIC_FILTERS_NOT_SELECTED } from '@/shared/constants';
import { dynamicFiltersUtils } from '../../../core/utils/dynamic-filters-utils';
import { IDateRange, IDynamicFilterItem, IDynamicFilterValueOptions } from '../../../stores/core/models';

const emit = defineEmits(['cancel', 'remove-filter', 'add-filter']);

const props = defineProps({
  filterKey: {
    type: String,
    required: true,
  },
  filterValue: {
    type: Object as PropType<string | string[] | IDateRange | undefined>,
  },
  filterValueOptions: {
    type: Object as PropType<IDynamicFilterValueOptions>,
  },
  enableClear: {
    type: Boolean,
  },
});

const isDark = useDark();

const isApplyBtnDisabled = computed(
  () =>
    props.filterKey === DYNAMIC_FILTERS_NOT_SELECTED ||
    props.filterValue === null ||
    (Array.isArray(props.filterValue) && props.filterValue.length === 0)
);

const buildFilterItem = () => {
  const filterItem: IDynamicFilterItem = {
    isDefault: false,
    key: props.filterKey,
    value: props.filterValue,
    type: props.filterValueOptions?.type,
    keyResourceId: props.filterValueOptions?.label || '',
    isStickyInternal: props.filterValueOptions?.isSticky || false,
    valueResourceId: dynamicFiltersUtils.getDynamicFilterValueResourceId(props.filterValueOptions, props.filterValue),
  };

  return filterItem;
};

const onApply = () => {
  const filterItem = buildFilterItem();
  emit('add-filter', filterItem);
};

const onRemove = () => {
  const filterItem = buildFilterItem();
  emit('remove-filter', filterItem);
};
</script>
<template>
  <app-divider />
  <div class="flex justify-center gap-3 px-4 py-1">
    <app-button
      :min-width="168"
      :icon-img="cancelImg"
      :icon-img-dark="cancelImgDark"
      label="core.cancel"
      use-contrast
      :severity="'secondary'"
      @click="$emit('cancel')"
    />
    <app-button
      icon="hugeicons:delete-03"
      label="core.remove"
      :severity="'danger'"
      @click="onRemove"
      v-if="enableClear"
    />
    <app-button
      use-contrast
      :min-width="168"
      :icon-img="addImg"
      :icon-img-dark="addImgDark"
      :label="enableClear ? 'core.apply' : 'core.add'"
      :severity="isDark ? 'success' : 'secondary'"
      :disabled="isApplyBtnDisabled"
      @click="onApply"
    />
  </div>
</template>
